import useToast from '@/hooks/useToast';
import { Input } from '@chakra-ui/react';
import React from 'react';
import Resizer from 'react-image-file-resizer';

type Props = {
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  setPreview?: (newPreview: string | null) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  fileSizeLimitInMBs?: number;
  fileMinHeightPx?: number;
  fileMinWidthPx?: number;
  accept?: 'image/*' | '.csv' | '.png, .jpeg, .jpg';
};

const MB = 1024 * 1024;

const FilePicker = ({
  inputRef,
  setPreview,
  setSelectedFile,
  fileSizeLimitInMBs = 10,
  fileMinHeightPx = 200,
  fileMinWidthPx = 200,
  accept = 'image/*',
}: Props) => {
  const toast = useToast();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const isImage = accept === 'image/*' || accept === '.png, .jpeg, .jpg';
      if (isImage) {
        if (file.size > fileSizeLimitInMBs * MB) {
          toast({
            title: 'File too large.',
            description: `File must be less than ${fileSizeLimitInMBs}MB.`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        // Check image dimensions
        const img = new Image();
        img.src = URL.createObjectURL(file);

        img.onload = () => {
          if (img.width < fileMinWidthPx || img.height < fileMinHeightPx) {
            toast({
              title: 'Image dimensions too small.',
              description: `Image must be at least ${fileMinWidthPx}x${fileMinHeightPx}.`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
            });
            return;
          }

          if (setPreview) {
            // Generate image preview
            const reader = new FileReader();
            reader.onloadend = () => {
              setPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
          }

          const fileSizeInMB = file.size / MB;
          // we need to limit the size to 1MB, so `100 / fileSizeInMB` - if the fileSizeInMB was 5MB, 100/5 = 20
          // so, the quality we will allow is 20% of the original photo
          const qualityPercentage = fileSizeInMB < 1 ? 90 : 100 / fileSizeInMB;

          // resize and setSelectedFile
          Resizer.imageFileResizer(
            file, // file of the image which will resized.
            4000, // maxWidth of the resized new image.
            4000, // maxHeight of the resized new image.
            'JPEG', // compressFormat of the resized new image.
            qualityPercentage, // quality of the resized new image (0-100)
            0, // degree of clockwise rotation to apply to uploaded image.
            (compressedImage) => {
              //  callBack function of the resized new image URI.

              // we have to do the setSelectedFile call here, because it's the only spot AFTER img-dimension check
              setSelectedFile(compressedImage as File);
            },
            'file', // output type of the resized new image.
            200, // minWidth of the resized new image.
            200, // minHeight of the resized new image.
          );
        };
      }

      const isCsv = accept === '.csv';
      if (isCsv) {
        if (file.type !== 'text/csv' || !file.name.endsWith('.csv')) {
          toast({
            title: 'Invalid file type.',
            description: 'Please upload a valid CSV file.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setSelectedFile(null);
          return;
        }
        setSelectedFile(file);
      }
    }
  };

  return <Input hidden type="file" accept={accept} ref={inputRef} onChange={handleFileChange} />;
};

export default FilePicker;
